/**
 * Pase de fotos del hero.
 *
 * Todas las fotos principales de los productos disponibles pasan en orden
 * aleatorio con un fundido cruzado largo. El orden se baraja en el navegador en
 * cada visita (Liquid no tiene aleatorio y la página se cachea), y la primera
 * foto ya llega elegida desde el servidor para que se pinte sin esperar a JS.
 *
 * Fundido cruzado de verdad: la foto nueva entra por encima de la anterior, que
 * sigue opaca debajo hasta que la nueva termina. Si las dos se fundieran a la
 * vez, a mitad de camino ambas estarían semitransparentes y se vería el fondo:
 * un parpadeo gris entre fotos.
 *
 * Carga bajo demanda: sólo la primera foto viene con srcset; las demás lo traen
 * en data-* y se completan justo antes de mostrarse, esperando a que estén
 * decodificadas. Nadie se descarga 38 fotos a pantalla completa para ver dos.
 *
 * Se detiene cuando no hay nadie mirando (pestaña oculta o hero fuera de
 * pantalla) y respeta a quien pide reducir movimiento: arranca en pausa. El
 * botón de pausa no es decoración: un contenido que se mueve solo más de cinco
 * segundos tiene que poder detenerse (WCAG 2.2.2).
 */

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function shuffle(list) {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

class ScHeroSlides extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.sc-hero__slide')];
    if (this.slides.length < 2) return;

    this.hero = this.closest('.sc-hero');
    this.interval = Number(this.dataset.interval) || 6000;
    this.fade = Number(this.dataset.fade) || 2000;
    this.style.setProperty('--sc-hero-fade', `${this.fade}ms`);

    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.queue = [];

    this.piece = this.hero?.querySelector('[data-hero-piece]');
    this.pieceTitle = this.piece?.querySelector('[data-hero-piece-title]');
    this.pauseButton = this.hero?.querySelector('[data-hero-pause]');

    this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.onScreen = true;

    if (this.pauseButton) {
      this.pauseButton.hidden = false;
      this.onPauseClick = () => {
        this.userPaused = !this.userPaused;
        this.syncPauseButton();
        this.schedule();
      };
      this.pauseButton.addEventListener('click', this.onPauseClick);
      this.syncPauseButton();
    }

    this.onVisibility = () => this.schedule();
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window && this.hero) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.schedule();
      });
      this.observer.observe(this.hero);
    }

    this.preloadUpcoming();
    this.schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.pauseButton?.removeEventListener('click', this.onPauseClick);
  }

  get running() {
    return !this.userPaused && this.onScreen && !document.hidden;
  }

  syncPauseButton() {
    this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
    this.pauseButton.setAttribute(
      'aria-label',
      this.userPaused ? 'Reanudar el pase de fotos' : 'Pausar el pase de fotos'
    );
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.running || this.busy) return;
    this.timer = setTimeout(() => this.advance(), this.interval);
  }

  /* Baraja todas menos la que está en pantalla, para no repetir foto seguida ni
     al empezar una vuelta nueva. Cada foto sale una vez por vuelta. */
  refillQueue() {
    this.queue = shuffle(this.slides.map((_, i) => i).filter((i) => i !== this.index));
  }

  upcoming() {
    if (this.queue.length === 0) this.refillQueue();
    return this.queue[0];
  }

  hydrate(slide) {
    const images = [...slide.querySelectorAll('img[data-srcset]')];
    for (const img of images) {
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.src;
      delete img.dataset.sizes;
    }
    const all = [...slide.querySelectorAll('img')];
    const decoded = Promise.all(all.map((img) => (img.decode ? img.decode().catch(() => {}) : null)));
    // Una foto que no llega en cinco segundos no puede congelar el pase.
    return Promise.race([decoded, wait(5000)]);
  }

  preloadUpcoming() {
    const next = this.slides[this.upcoming()];
    if (next) this.hydrate(next);
  }

  async advance() {
    if (this.busy || !this.running) return;
    this.busy = true;

    const nextIndex = this.upcoming();
    const next = this.slides[nextIndex];
    const current = this.slides[this.index];

    await this.hydrate(next);

    if (!this.running) {
      this.busy = false;
      return;
    }

    this.queue.shift();
    next.removeAttribute('aria-hidden');
    next.classList.add('is-entering');
    this.updatePiece(next);

    await wait(this.fade);

    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');
    next.classList.add('is-active');
    next.classList.remove('is-entering');
    this.index = nextIndex;

    this.busy = false;
    this.preloadUpcoming();
    this.schedule();
  }

  /* El nombre de la pieza cambia con la foto: se apaga, cambia y vuelve, en la
     primera mitad del fundido para que nunca nombre una foto que ya se fue. */
  async updatePiece(slide) {
    if (!this.piece || !this.pieceTitle) return;
    this.piece.classList.add('is-changing');
    await wait(Math.min(500, this.fade / 2));
    this.pieceTitle.textContent = slide.dataset.title || '';
    if (slide.dataset.url) this.piece.href = slide.dataset.url;
    this.piece.classList.remove('is-changing');
  }
}

if (!customElements.get('sc-hero-slides')) {
  customElements.define('sc-hero-slides', ScHeroSlides);
}
